import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { keys } from '../services/queryKeys'
import { getPartner } from '../services/partnerService'
import Button from '../components/common/Button'
import { Empty, Failed, Loading } from '../components/common/States'

/**
 * The distance the worked example uses.
 *
 * Five kilometres is a real trip across Los Baños rather than a round number that flatters
 * the rate — far enough past any sensible `kmIncludedInBase` that the per-km line actually
 * appears in the arithmetic, which is the line customers want to see applied.
 */

export default function PartnerDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const { isPending: loading, data, error } = useQuery({
    queryKey: keys.partners.detail(slug),
    queryFn: () => getPartner(slug),
  })
  const partner = data?.data

  if (loading) return <Loading label="Loading partner…" />

  // A paused partner and an unknown one arrive the same way, so they read the same.
  if (error?.status === 404) {
    return (
      <Empty
        title="This partner is not available"
        note="They may have paused new orders, or the link may be out of date."
        action={
          <Link
            to="/home"
            className="text-body-s font-semibold text-brand underline underline-offset-4"
          >
            Browse other services
          </Link>
        }
      />
    )
  }

  if (error) return <Failed error={error} />


  return (
    <article className="px-5 pt-4 pb-7">
      <Link
        to="/home"
        className="inline-flex items-center min-h-[32px] text-body-s text-ink-3 transition-colors duration-quick hover:text-brand"
      >
        ← All services
      </Link>

      {/* The one screen where the business name is the subject, so the one
          screen where it is allowed to be set in Playfair. */}
      <h1 className="mt-3.5 font-display font-bold text-display-m text-ink">
        {partner.businessName}
      </h1>
      <p className="mt-1 text-body-s text-ink-3">
        {partner.barangay}, {partner.city}
      </p>

      {/* Their own words, unclamped here — the list had to cut it to two lines
          and this is the screen a customer opened to read the rest. It sits
          above the evidence blocks because what a service does is the question;
          whether the page is real is the follow-up. */}
      {partner.description && (
        <p className="mt-3.5 text-body text-ink-2">{partner.description}</p>
      )}

      <div className="mt-5 border border-hairline rounded-card overflow-hidden">
        <div className="px-4 py-3.5 border-b border-hairline">
          <p className="text-label font-semibold uppercase text-ink-3">Their page</p>
          {/* Evidence first, link second: it reads as proof the business is real,
              but it still opens, because a customer who wants to check will. */}
          <a
            href={partner.facebookPageUrl}
            target="_blank"
            rel="noreferrer"
            className="block mt-1 text-body text-ink break-all no-underline hover:underline focus-visible:underline underline-offset-4"
          >
            {partner.facebookPageUrl.replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        </div>

        <div className="px-4 py-3.5 bg-surface-2">
          <p className="text-label font-semibold uppercase text-ink-3">Serves</p>
          <p className="mt-1 text-body text-ink">
            {partner.barangay} and nearby areas in {partner.city}
          </p>
        </div>
      </div>

      {/**
        * The rate card used to sit here, and it has been removed.
        *
        * It was right when it was written: the fee could not be known before ordering,
        * so a base rate, a per-km rate and a worked example were the best answer
        * available — better than placing an order to find out.
        *
        * The summary quotes the **exact** fee now, before the customer commits, with the
        * rider's own trip already inside it. Two answers to one question is worse than
        * one answer late: somebody who reads "P49 base, P12/km, up to P60 more" here and
        * then meets P104.50 on the summary has been handed arithmetic to check rather
        * than a price to accept, and the two can only ever agree by coincidence.
        *
        * The pricing still arrives on this endpoint and the order form still uses it.
        * It is this screen that has nothing useful left to do with it.
        */}

      <p className="mt-[18px] text-body-s text-ink-2">
        You say what to buy and where. Their rider fetches it.
      </p>

      <Button onClick={() => navigate(`/order/${partner.slug}`)} className="w-full mt-5">
        Order from {partner.businessName.split(' ')[0]}
      </Button>
    </article>
  )
}
